import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Tag,
  Select,
  Button,
  Input,
  message,
  Space,
  DatePicker,
  Divider,
  Avatar,
  Modal,
  Dropdown,
  InputNumber,
  Switch,
  Grid,
} from "antd";

const { useBreakpoint } = Grid;

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  FlagOutlined,
  CalendarOutlined,
  LinkOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";

import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  updateTaskApi,
  addTaskActivityApi,
  getTaskActivitiesApi,
  rescheduleTaskApi,
  getTaskDetailsApi,
  saveConversationApi,
  snoozeTaskApi,
  completeTaskApi,
  updateLeadMetaApi,
} from "../../../../apiservice/crm";
import { convertToCompanyTimezoneObject, formatDayjsToUtc } from "../../../../lib/functions";
import { resolveDialableNumber, resolveWhatsAppNumber } from "../../../../lib/phone";
import LeadStatusSelect from "../../Leads/components/LeadStatusSelect";
import moment from "moment";
import "../../crmSharedStyle.css";
import "../crmTasksStyle.css";

dayjs.extend(relativeTime);

const { TextArea } = Input;

const infoCardStyle = {
  borderRadius: 14,
  textAlign: "center",
  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  height: "100%",
};

const PRIORITY_OPTIONS = [
  { label: "High", value: "high", color: "red" },
  { label: "Medium", value: "medium", color: "orange" },
  { label: "Low", value: "low", color: "green" },
];

const priorityMeta = (value) =>
  PRIORITY_OPTIONS.find((o) => o.value === value) || PRIORITY_OPTIONS[2];

// Status/type are stored as lowercase codes (e.g. "pending", "followup");
// this only covers generic word-casing -- task.type_name (resolved from
// crm_task_type_master, which already carries its own admin-defined label
// such as "Follow-up") is used in place of this wherever available.
const formatLabel = (value) =>
  String(value || "")
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const TaskDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [task, setTask] = useState(null);
  const [activities, setActivities] = useState([]);
  const [note, setNote] = useState("");
  const [completeModal, setCompleteModal] = useState(false);
  const [completeRemark, setCompleteRemark] = useState("");
  const [completing, setCompleting] = useState(false);
  const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
  const [customMinutes, setCustomMinutes] = useState(30);
  const [selectedTask, setSelectedTask] = useState(null);
  const [scheduleNextFollowup, setScheduleNextFollowup] = useState(false);
  const [nextFollowupDate, setNextFollowupDate] = useState(null);
  const [nextFollowupRemarks, setNextFollowupRemarks] = useState("");
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [priorityUpdating, setPriorityUpdating] = useState(false);
  const reschedulePanelRef = useRef(null);

  const load = async () => {
    try {
      const res = await getTaskDetailsApi({
        task_id: id,
      });

      setTask(res.data);
    } catch {
      message.error("Failed to load task");
    }
  };

  const loadActivities = async () => {
    try {
      const res = await getTaskActivitiesApi({
        task_id: id,
      });

      setActivities(res.data || []);
    } catch {
      console.log("activity load failed");
    }
  };

  useEffect(() => {
    load();
    loadActivities();
  }, [id]);

  const updateStatus = async (status) => {
    try {
      await updateTaskApi({
        task_id: id,
        status,
      });

      setTask((prev) => ({
        ...prev,
        status,
      }));

      message.success("Task updated");
    } catch {
      message.error("Update failed");
    }
  };

  // Updates the RELATED LEAD's own status -- a separate concept from this
  // task's own Status/Priority above, via the same endpoint the Kanban board
  // and the Lead Info tab already use to set a lead's status.
  const updateLeadStatus = async (statusId) => {
    if (!task?.lead_id || !statusId) return;
    const previous = task.status_id;
    setTask((prev) => ({ ...prev, status_id: statusId })); // optimistic
    try {
      await updateLeadMetaApi({ lead_id: task.lead_id, status_id: statusId });
      const refreshed = await getTaskDetailsApi({ task_id: id });
      if (refreshed?.data) setTask(refreshed.data);
      message.success("Lead status updated");
    } catch {
      setTask((prev) => ({ ...prev, status_id: previous }));
      message.error("Failed to update lead status");
    }
  };

  const updatePriority = async (priority) => {
    const previous = task.priority;
    setTask((prev) => ({ ...prev, priority })); // optimistic — revert below on failure
    setPriorityUpdating(true);
    try {
      await updateTaskApi({ task_id: id, priority });
      message.success("Priority updated");
    } catch {
      setTask((prev) => ({ ...prev, priority: previous }));
      message.error("Failed to update priority");
    } finally {
      setPriorityUpdating(false);
    }
  };

  const openReschedule = () => {
    // The helper returns a moment object; antd's DatePicker needs dayjs, and
    // passing moment makes the calendar jump to random months and years.
    const due = task.due_date ? convertToCompanyTimezoneObject(task.due_date) : null;
    setRescheduleDate(
      due && due.isValid()
        ? dayjs(due.format("YYYY-MM-DDTHH:mm:ss"))
        : dayjs().add(1, "day")
    );
    setRescheduleOpen(true);
  };

  // dateOverride: antd's DatePicker onOk hands back the picked value
  // directly -- using that instead of the rescheduleDate state avoids a
  // stale read if state hasn't flushed yet by the time OK is clicked.
  const confirmReschedule = async (dateOverride) => {
    const date = dateOverride || rescheduleDate;
    if (!date) {
      return message.warning("Pick a date and time");
    }

    setRescheduling(true);
    try {
      await rescheduleTaskApi({
        task_id: task.task_id,
        due_date: formatDayjsToUtc(date.second(0)),
      });

      message.success("Task rescheduled");
      setRescheduleOpen(false);
      load();
    } catch {
      message.error("Failed to reschedule task");
    } finally {
      setRescheduling(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;

    try {
      await addTaskActivityApi({
        task_id: id,
        text: note,
      });

      setNote("");

      loadActivities();

      message.success("Update added");
    } catch {
      message.error("Failed");
    }
  };

  const snoozeTask = async (taskId, minutes) => {
      await snoozeTaskApi({
        task_id: taskId,
        minutes,
      });
  
      message.success(
        `Task snoozed by ${minutes} minute(s)`
      );
  
      load();
    };
  
    const snoozeMenu = (task) => ({
      items: [
        {
          key: "30",
          label: "30 minutes",
          onClick: () => snoozeTask(task.task_id, 30),
        },
        {
          key: "60",
          label: "1 hour",
          onClick: () => snoozeTask(task.task_id, 60),
        },
        {
          key: "120",
          label: "2 hours",
          onClick: () => snoozeTask(task.task_id, 120),
        },
        {
          type: "divider",
        },
        {
          key: "custom",
          label: "Custom...",
          onClick: () => {
            setSelectedTask(task);
            setCustomMinutes(30);
            setCustomSnoozeOpen(true);
          },
        },
      ],
    });

  if (!task) {
    return (
      <div className="p-4">
        <Card loading style={{ borderRadius: 16 }} />
      </div>
    );
  }

  const overdue =
    !["completed", "done", "cancelled"].includes(task.status) &&
    task.due_date &&
    convertToCompanyTimezoneObject(task.due_date)
        .isBefore(dayjs());

  return (
    <div
      className="p-4"
      style={{
        maxWidth: 1400,
        margin: "0 auto",
      }}
    >
      <Row gutter={[20, 20]}>
        {/* LEFT SIDE */}
        <Col xs={24} lg={16}>
          {/* HEADER */}
          <Card
            style={{
              borderRadius: 18,
              boxShadow: "0 3px 14px rgba(0,0,0,0.06)",
            }}
          >
            <Row justify="space-between" align="top" gutter={[0, 16]}>
              <Col>
              <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                  style={{ paddingLeft: 0, color: "#8c8c8c", height: "auto", marginBottom: 8 }}
                >

                </Button>

                <div
                  style={{
                    fontSize: 13,
                    color: "#888",
                    fontWeight: 600,
                    letterSpacing: 1,
                    marginBottom: 8,
                  }}
                >
                  TASK DETAILS
                </div>

                <h1
                  style={{
                    fontSize: 30,
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  {task.type_name || formatLabel(task.type)}
                </h1>

                <div
                  className="crm-user-text"
                  style={{
                    color: "#666",
                    fontSize: 15,
                    marginBottom: 16,
                  }}
                >
                  {task.remarks || "No remarks"}
                </div>

                <Tag
                  color={
                    task.bucket === "completed"
                      ? "green"
                      : task.bucket === "overdue"
                      ? "red"
                      : task.bucket === "today"
                      ? "orange"
                      : task.bucket === "cancelled"
                      ? "default"
                      : "blue"
                  }
                  style={{
                    borderRadius: 999,
                    paddingInline: 14,
                    paddingBlock: 4,
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {overdue ? "OVERDUE" : task.status?.toUpperCase()}
                </Tag>
              </Col>

              <Col xs={24} sm="auto" style={{ width: isMobile ? "100%" : undefined }}>
                {task.status !== "completed" && task.status !== "cancelled" && (
                <div className="crm-task-header-actions">
                  <Button
                    icon={<CalendarOutlined />}
                    size="large"
                    style={isMobile ? { flex: "1 1 0", minWidth: 0 } : undefined}
                    onClick={openReschedule}
                  >
                    Reschedule
                  </Button>

                  {task.status === "pending" &&
                    (task.bucket === "today" ||
                      task.bucket === "overdue") && (
                      <Dropdown
                          menu={snoozeMenu(task)}
                          trigger={["click"]}
                      >
                          <Button
                              icon={<ClockCircleOutlined />}
                              size="large"
                              style={isMobile ? { flex: "1 1 0", minWidth: 0 } : undefined}
                          >
                              Snooze
                          </Button>
                      </Dropdown>
                    )}

                 <Button
                  type="primary"
                  size="large"
                  style={isMobile ? { flex: "1 1 0", minWidth: 0 } : undefined}
                  icon={<CheckCircleOutlined />}
                  onClick={() => setCompleteModal(true)}  // ← open modal instead
                >
                  Complete
                </Button>
                </div>
                )}
              </Col>
            </Row>

            <Divider />

            {/* KPI ROW */}
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Card bordered={false} style={infoCardStyle}>
                  <FlagOutlined
                    style={{
                      fontSize: 20,
                      marginBottom: 8,
                    }}
                  />

                  <div
                    style={{
                      color: "#888",
                      fontSize: 12,
                    }}
                  >
                    STATUS
                  </div>

                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 18,
                    }}
                  >
                    {formatLabel(task.status)}
                  </div>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card bordered={false} style={infoCardStyle}>
                  <FlagOutlined
                    style={{
                      fontSize: 20,
                      marginBottom: 8,
                      color: priorityMeta(task.priority).color,
                    }}
                  />

                  <div
                    style={{
                      color: "#888",
                      fontSize: 12,
                    }}
                  >
                    PRIORITY
                  </div>

                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 18,
                      color: priorityMeta(task.priority).color,
                    }}
                  >
                    {priorityMeta(task.priority).label}
                  </div>
                </Card>
              </Col>

              <Col xs={24} md={8}>
                <Card bordered={false} style={infoCardStyle}>
                  <FileTextOutlined
                    style={{
                      fontSize: 20,
                      marginBottom: 8,
                    }}
                  />

                  <div
                    style={{
                      color: "#888",
                      fontSize: 12,
                    }}
                  >
                    UPDATES
                  </div>

                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 18,
                    }}
                  >
                    {activities.length}
                  </div>
                </Card>
              </Col>
            </Row>

            <Divider />

            {/* DETAILS */}
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <div style={{ color: "#888" }}>
                  Due Date
                </div>

                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  {task.due_date
                    ? convertToCompanyTimezoneObject(task.due_date).format(
                        "DD MMM YYYY hh:mm A"
                      )
                    : "-"}
                </div>

                <div
                  style={{
                    color: overdue ? "#ff4d4f" : "#888",
                    marginTop: 4,
                  }}
                >
                  {task.due_date
                    ? convertToCompanyTimezoneObject(task.due_date).fromNow()
                    : ""}
                </div>
              </Col>

              <Col xs={24} md={8}>
                <div style={{ color: "#888" }}>
                  Assigned To
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 6,
                  }}
                >
                  <Avatar icon={<UserOutlined />} />

                  <div
                    style={{
                      fontWeight: 600,
                    }}
                  >
                    {task.assigned_user || "Unassigned"}
                  </div>
                </div>
              </Col>

              <Col xs={24} md={8}>
                <div style={{ color: "#888" }}>
                  Created
                </div>

                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                  }}
                >
                  {convertToCompanyTimezoneObject(task.created_at).format(
                    "DD MMM YYYY"
                  )}
                </div>

                <div
                  style={{
                    color: "#888",
                    marginTop: 4,
                  }}
                >
                  {convertToCompanyTimezoneObject(task.created_at).fromNow()}
                </div>
              </Col>
            </Row>
          </Card>

          {/* ACTIVITY */}
          <Card
            title="Task Activity"
            style={{
              marginTop: 20,
              borderRadius: 18,
              boxShadow: "0 3px 14px rgba(0,0,0,0.06)",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 20,
              }}
            >
              <TextArea
                className="crm-user-text"
                rows={3}
                value={note}
                onChange={(e) =>
                  setNote(e.target.value)
                }
                placeholder="Add update..."
              />

              <Button
                type="primary"
                onClick={addNote}
              >
                Send
              </Button>
            </div>

            {activities.length ? (
              activities.map((a) => (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    gap: 14,
                    marginBottom: 20,
                  }}
                >
                  {/* TIMELINE */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: "#1677ff",
                        marginTop: 6,
                      }}
                    />

                    <div
                      style={{
                        width: 2,
                        flex: 1,
                        background: "#e5e5e5",
                        marginTop: 4,
                      }}
                    />
                  </div>

                  {/* CONTENT */}
                  <div
                    style={{
                      flex: 1,
                      background: "#f7f7f7",
                      padding: 14,
                      borderRadius: 14,
                    }}
                  >
                    <div
                      className="crm-user-text"
                      style={{
                        fontSize: 15,
                      }}
                    >
                      {a.text}
                    </div>

                    <div
                      style={{
                        marginTop: 8,
                        fontSize: 12,
                        color: "#888",
                      }}
                    >
                      {convertToCompanyTimezoneObject(a.created_at).format(
                        "DD MMM YYYY hh:mm A"
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: "center",
                  color: "#999",
                  padding: 30,
                }}
              >
                No activity yet
              </div>
            )}
          </Card>
        </Col>

        {/* RIGHT SIDE */}
        <Col xs={24} lg={8}>
          {/* TASK CONTROLS */}
          <Card
            title="Task Controls"
            style={{
              borderRadius: 18,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  marginBottom: 6,
                  color: "#888",
                }}
              >
                Status
              </div>

              <Select
                style={{ width: "100%" }}
                value={task.status}
                onChange={(value) => {
                  if (value === "completed") {
                    // Do not update immediately.
                    // Open the completion/follow-up modal instead.
                    setCompleteModal(true);
                    return;
                  }

                  updateStatus(value);
                }}
                disabled={task.status === "completed"}
                options={[
                  {
                    label: "Pending",
                    value: "pending",
                  },
                  {
                    label: "Completed",
                    value: "completed",
                  },
                  {
                    label: "Cancelled",
                    value: "cancelled",
                  },
                ]}
              />
            </div>

            <div>
              <div
                style={{
                  marginBottom: 6,
                  color: "#888",
                }}
              >
                Priority
              </div>

              <Select
                className="crm-priority-select"
                style={{ width: "100%" }}
                value={task.priority}
                loading={priorityUpdating}
                onChange={updatePriority}
                options={PRIORITY_OPTIONS.map((o) => ({
                  value: o.value,
                  label: (
                    <Space size={6}>
                      <span className={`crm-priority-dot ${o.value}`} />
                      {o.label}
                    </Space>
                  ),
                }))}
              />
            </div>
          </Card>

          {/* RELATED ENTITIES */}
          <Card
            title="Related Records"
            style={{
              borderRadius: 18,
            }}
          >
            <Space
              direction="vertical"
              style={{ width: "100%" }}
              size={16}
            >
              {task.lead_id && (
                <Card
                  hoverable
                  onClick={() => navigate(`/crm/leads/${task.lead_id}`)}
                  style={{ borderRadius: 14 }}
                >
                  <div style={{ display: "flex", gap: 10 }}>
                    <LinkOutlined
                      style={{
                        fontSize: 18,
                        color: "#1677ff",
                        marginTop: 2,
                      }}
                    />

                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          color: "#888",
                          fontSize: 12,
                          marginBottom: 8,
                        }}
                      >
                        RELATED LEAD
                      </div>

                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 17,
                          marginBottom: 12,
                        }}
                      >
                        {task.lead_name}
                      </div>

                      <div
                        style={{
                          marginBottom: 8,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        <strong>Phone:</strong>

                        {task.contact_no ? (
                          <>
                            <a
                              href={`tel:${resolveDialableNumber(task.contact_no, task.lead_country)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              style={{
                                color: "#1677ff",
                                fontWeight: 500,
                                textDecoration: "none",
                              }}
                            >
                              {task.contact_no}
                            </a>

                            <Button
                              type="primary"
                              size="small"
                              icon={<PhoneOutlined />}
                              href={`tel:${resolveDialableNumber(task.contact_no, task.lead_country)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              style={{
                                borderRadius: 6,
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              Call
                            </Button>

                            <Button
                              type="primary"
                              size="small"
                              icon={<WhatsAppOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`https://wa.me/${resolveWhatsAppNumber(task.contact_no, task.lead_country)}`);
                              }}
                              style={{
                                borderRadius: 6,
                                display: "inline-flex",
                                alignItems: "center",
                                background: "#25D366",
                                borderColor: "#25D366",
                              }}
                            >
                              WhatsApp
                            </Button>
                          </>
                        ) : (
                          <span style={{ color: "#999" }}>-</span>
                        )}
                      </div>

                      <div style={{ marginBottom: 8 }} onClick={(e) => e.stopPropagation()}>
                        <strong>Lead Status:</strong>
                        <div style={{ marginTop: 4, maxWidth: 220 }}>
                          <LeadStatusSelect
                            value={task.status_id}
                            onChange={updateLeadStatus}
                            placeholder="Lead status"
                          />
                        </div>
                      </div>

                      <div style={{ marginBottom: 8 }}>
                        <strong>Source:</strong> {task.lead_source || "-"}
                      </div>

                      <div>
                        <strong>Purpose:</strong> {task.lead_purpose || "-"}
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {task.deal_id && (
                <Card
                  hoverable
                  onClick={() =>
                    navigate(
                      `/crm/deals/${task.deal_id}`
                    )
                  }
                  style={{
                    borderRadius: 14,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                    }}
                  >
                    <LinkOutlined
                      style={{
                        fontSize: 18,
                        color: "#52c41a",
                      }}
                    />

                    <div>
                      <div
                        style={{
                          color: "#888",
                          fontSize: 12,
                        }}
                      >
                        RELATED DEAL
                      </div>

                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 16,
                        }}
                      >
                        {task.deal_title}
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </Space>
          </Card>
        </Col>
      </Row>
      <Modal
        title="Complete Task"
        open={completeModal}
        onCancel={() => {
          if (completing) return;

          setCompleteModal(false);
          setCompleteRemark("");
          setScheduleNextFollowup(false);
          setNextFollowupDate(null);
          setNextFollowupRemarks("");
        }}
        confirmLoading={completing}
        okText={
          scheduleNextFollowup
            ? "Complete & Schedule Follow-up"
            : "Complete Task"
        }
        onOk={async () => {
          if (scheduleNextFollowup && !nextFollowupDate) {
            return message.warning("Select the next follow-up date");
          }

          try {
            setCompleting(true);

            await completeTaskApi({
              task_id: task.task_id,
              completion_remark: completeRemark.trim() || null,
              schedule_next_followup: scheduleNextFollowup,
              next_followup_date:
                scheduleNextFollowup && nextFollowupDate
                  ? formatDayjsToUtc(nextFollowupDate)
                  : null,
              next_followup_remarks:
                scheduleNextFollowup
                  ? nextFollowupRemarks.trim() || null
                  : null,
            });

            message.success(
              scheduleNextFollowup
                ? "Task completed and next follow-up scheduled"
                : "Task completed successfully"
            );

            setCompleteModal(false);
            setCompleteRemark("");
            setScheduleNextFollowup(false);
            setNextFollowupDate(null);
            setNextFollowupRemarks("");

            await load();
            await loadActivities();
          } catch (err) {
            message.error(
              err?.response?.data?.message || "Failed to complete task"
            );
          } finally {
            setCompleting(false);
          }
        }}
      >
        <Input.TextArea
          className="crm-user-text"
          autoSize={{ minRows: 3, maxRows: 10 }}
          placeholder="Add a completion note (optional)"
          value={completeRemark}
          onChange={(e) => setCompleteRemark(e.target.value)}
          style={{ marginBottom: 20 }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <div style={{ fontWeight: 600 }}>
              Schedule Next Follow-up
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#888",
                marginTop: 2,
              }}
            >
              Create another follow-up task after completing this one
            </div>
          </div>

          <Switch
            checked={scheduleNextFollowup}
            onChange={setScheduleNextFollowup}
          />
        </div>

        {scheduleNextFollowup && (
          <>
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD/MM/YYYY HH:mm"
              style={{
                width: "100%",
                marginBottom: 12,
              }}
              placeholder="Next follow-up date and time"
              value={nextFollowupDate}
              onChange={(v) => setNextFollowupDate(v ? v.second(0) : v)}
              disabledDate={(current) =>
                current && current.endOf("day").isBefore(dayjs())
              }
            />

            <Input.TextArea
              className="crm-user-text"
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="Next follow-up remarks (optional)"
              value={nextFollowupRemarks}
              onChange={(e) =>
                setNextFollowupRemarks(e.target.value)
              }
            />
          </>
        )}
      </Modal>

  <Modal
    title="Custom Snooze"
    open={customSnoozeOpen}
    onCancel={() => setCustomSnoozeOpen(false)}
    onOk={async () => {
      if (customMinutes < 1) {
        return message.warning(
          "Minutes should be greater than zero."
        );
      }

      await snoozeTask(
        selectedTask.task_id,
        customMinutes
      );

      setCustomSnoozeOpen(false);
    }}
  >
    <div style={{ marginBottom: 10 }}>
      Snooze for
    </div>

    <InputNumber
      min={1}
      max={1440}
      value={customMinutes}
      onChange={setCustomMinutes}
      style={{ width: "100%" }}
      addonAfter="minutes"
    />
  </Modal>

      {/* RESCHEDULE — the calendar panel's own OK both picks the date/time
          and submits the reschedule in one click; the modal has no footer
          of its own, so there's nothing left to click afterward. Cancelling
          is just the modal's own close (X) / backdrop click.
          No live "new due date" preview here: with showTime, antd only
          fires onChange once OK is clicked -- and OK immediately submits
          and closes this modal, so a preview could never show anything
          other than the stale initial value before vanishing. */}
      <Modal
        title="Reschedule task"
        open={rescheduleOpen}
        onCancel={() => setRescheduleOpen(false)}
        footer={null}
        className="crm-reschedule-modal"
        destroyOnClose
      >
        {task.due_date && (
          <div className="crm-reschedule-current">
            Currently due{" "}
            {convertToCompanyTimezoneObject(task.due_date).format("DD MMM YYYY, hh:mm A")}
          </div>
        )}

        <div ref={reschedulePanelRef} className="crm-reschedule-panel">
          <DatePicker
            open
            disabled={rescheduling}
            showTime={{ format: "HH:mm" }}
            format="DD/MM/YYYY HH:mm"
            value={rescheduleDate}
            onChange={setRescheduleDate}
            onOk={(value) => confirmReschedule(value)}
            getPopupContainer={() => reschedulePanelRef.current}
            style={{ width: "100%" }}
            disabledDate={(current) =>
              current && current.endOf("day").isBefore(dayjs())
            }
          />
        </div>
      </Modal>
    </div>
  );
};

export default TaskDetails;